/**
 * 统一的 API 响应类型定义
 * 与后端 background/src/types/api.ts 保持一致
 */

import type { VideoState, VideoError } from './status.js';

/**
 * 标准 API 响应包装器
 */
export interface ApiResponse<T = any> {
  /** 请求是否成功 */
  success: boolean;
  /** 响应数据 */
  data?: T;
  /** 错误信息（仅当 success === false 时存在） */
  error?: string;
  /** 错误代码（可选，用于标准化错误处理） */
  errorCode?: string;
}

/**
 * 标准化错误信息
 */
export interface StandardizedError {
  /** 用户友好的错误消息 */
  message: string;
  /** 错误代码（用于程序化处理） */
  code: string;
  /** HTTP状态码（如果适用） */
  statusCode?: number;
  /** 原始错误信息（用于调试） */
  originalError?: any;
}

/**
 * 错误代码到用户友好消息的映射
 */
export const ERROR_CODE_MESSAGES: Record<string, string> = {
  // 认证相关
  UNAUTHORIZED: '请先登录',
  INVALID_CREDENTIALS: '用户名或密码错误',
  USER_NOT_FOUND: '用户不存在',
  FORBIDDEN: '权限不足',

  // 注册相关
  USER_EXISTS: '用户名已存在',
  EMAIL_EXISTS: '邮箱已被注册',
  INVALID_INVITE_CODE: '邀请码无效',
  VALIDATION_ERROR: '输入信息格式错误',

  // 文件上传相关
  NO_FILE_UPLOADED: '未选择文件',
  FILE_TOO_LARGE: '文件大小超过限制',
  UPLOAD_QUOTA_EXCEEDED: '上传配额已用完',

  // 视频相关
  VIDEO_NOT_FOUND: '视频不存在',
  VIDEO_DURATION_EXCEEDED: '视频时长超过限制',

  // 通用错误
  INTERNAL_ERROR: '服务器内部错误',
  NETWORK_ERROR: '网络连接错误',
  TIMEOUT_ERROR: '请求超时',

  // 默认错误
  UNKNOWN_ERROR: '未知错误，请稍后重试'
};

/**
 * 资源 URL 信息
 * 支持多种存储后端（本地文件系统、HLS、云存储）
 */
export interface AssetUrl {
  /** 资源类型（语义类型） */
  type: 'video' | 'audio' | 'hls' | 'subtitle_original' | 'subtitle_translated' | 'words' | 'display_words';
  /** 资源访问 URL（统一入口，不暴露存储实现） */
  url: string;
  /** 存储类型（当前实现：'local'，未来可能：'hls' | 's3' | 'oss'） */
  storageType?: 'local' | 'hls' | 's3' | 'oss';
  /** MIME 类型 */
  mimeType?: string;
  /** 文件大小（字节，可选） */
  size?: number;
  /** 是否支持 Range 请求（视频/音频通常需要） */
  supportsRange?: boolean;
}

/**
 * 视频资源信息
 * 包含所有可用的资源 URL
 */
export interface VideoAssets {
  /** 视频资源 URL */
  video?: AssetUrl;
  /** 音频资源 URL */
  audio?: AssetUrl;
  /** HLS 流媒体资源 URL */
  hls?: AssetUrl;
  /** 原文字幕资源 URL */
  subtitle_original?: AssetUrl;
  /** 翻译字幕资源 URL */
  subtitle_translated?: AssetUrl;
  /** 词级对齐 JSON 资源 URL */
  words?: AssetUrl;
  /** 词级高亮视图 JSON 资源 URL（display_words.json） */
  display_words?: AssetUrl;
}

/**
 * 资源可用性信息（布尔值，用于快速检查）
 */
export interface AssetAvailability {
  /** 视频是否可用 */
  video: boolean;
  /** 音频是否可用 */
  audio: boolean;
  /** 原文字幕是否可用 */
  subtitle_original: boolean;
  /** 翻译字幕是否可用 */
  subtitle_translated: boolean;
  /** 词级对齐 JSON 是否可用 */
  words: boolean;
}

/**
 * 上传响应数据
 */
export interface UploadResponseData {
  /** 视频 ID（语义标识符，目录名） */
  videoId: string;
  /** 目标语言代码 */
  targetLang: string;
  /** 源语言代码（可选） */
  sourceLang?: string | undefined;
  /** 视频资源 URL（如果上传的是视频文件） */
  videoUrl?: AssetUrl | undefined;
  /** 当前用户剩余上传次数（可选，upload 接口在更新后返回以避免额外的 /auth/me 请求） */
  remainingUploads?: number | undefined;
}

/**
 * 处理响应数据
 */
export interface ProcessResponseData {
  /** 视频 ID */
  videoId: string;
  /** 处理状态消息 */
  message: string;
}

/**
 * 状态检查响应数据
 * 
 * 【边界 2｜check-status 的职责限制】
 * - check-status 只能返回轻量级状态信息（用于轮询）
 * - 不允许返回 assets, assetUrls, isComplete, playable
 * - 任何完整 UI 状态都必须通过返回 VideoDescriptor 的 API 获取
 */
export interface StatusResponseData {
  /** 视频 ID */
  videoId: string;
  /** 当前处理状态 */
  state: VideoState;
  /** 处理进度（0-100） */
  progress: number;
  /** 给 UI 的简短说明 */
  message: string;
  /** 错误信息（如果处理失败） */
  error?: string | null;
}

/**
 * 视频列表项
 */
export interface VideoListItem {
  /** 视频 ID */
  videoId: string;
  /** 资源可用性信息 */
  assets: AssetAvailability;
  /** 是否有输出文件（向后兼容，等价于 assets.audio && assets.subtitle_original && assets.subtitle_translated） */
  hasOutput?: boolean;
  /** 是否有 JSON 文件（向后兼容，等价于 assets.words） */
  hasJson?: boolean;
}

/**
 * 视频列表响应数据
 * 
 * 【边界 1】：使用 VideoDescriptor 作为唯一真相来源
 */
/** 统一的视频列表响应（包含分页信息） */
export interface VideoListResponseData {
  list: VideoDescriptor[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

// 向后兼容别名（历史名称）
export type VideoListResponse = VideoListResponseData;

/**
 * 视频元数据响应数据
 * @deprecated 请使用 VideoDescriptor
 */
export interface VideoMetadataResponseData {
  /** 视频 ID */
  videoId: string;
  /** 资源可用性信息 */
  assets: AssetAvailability;
  /** 可用的资源 URL */
  assetUrls: VideoAssets;
}

/**
 * 视频聚合模型（UI 唯一真相来源）
 * 
 * 【边界 1｜唯一读模型】
 * - VideoDescriptor 是 UI 层唯一允许使用的读模型
 * - UI 不允许再自行组合数据库字段 / 资产信息 / 文件存在性
 * - 所有"是否可播放 / 是否有能力 / 状态语义判断"只能在后端 VideoDescriptor 中派生
 * 
 * 【边界 3｜metadata.files 的使用限制】
 * - files 字段属于内部实现细节
 * - 前端不得依赖 files 进行任何业务判断或 UI 渲染
 * - files 仅允许用于调试或开发期观察
 * - 后续允许在不算 breaking change 的情况下从 VideoDescriptor 中移除 files
 * 
 * 字段来源：
 * - 数据库字段: videoId, sourceLanguage, targetLanguages, createdAt, state, progress, message, error, updatedAt
 * - 运行时派生: isCompleted, isFailed, isProcessing, playable, capabilities, assetUrls
 */
export interface VideoDescriptor {
  // ========== 来自 metadata.json ==========
  /** 视频 ID（目录名） */
  videoId: string;
  /** 视频显示名称（用户可自定义） */
  name?: string;
  /** 源语言代码（ISO 639-1） */
  sourceLanguage: string;
  /** 目标语言代码数组（ISO 639-1） */
  targetLanguages: string[];
  /**
   * 词级高亮使用的数据源：
   * - 'display'：display_words.json（合并/修复后的视图层）
   * - 'raw'    ：words.json（WhisperX 原始词级对齐；新视频默认）
   */
  wordHighlightMode?: 'raw' | 'display';
  /** 
   * 文件映射（相对路径）
   * 
   * ⚠️ 边界 3：此字段属于内部实现细节
   * - 前端不得依赖 files 进行任何业务判断或 UI 渲染
   * - files 仅允许用于调试或开发期观察
   * - 后续允许在不算 breaking change 的情况下从 VideoDescriptor 中移除 files
   */
  files: {
    /** 视频文件 */
    video: string;
    /** 字幕文件（按语言代码索引） */
    subtitles: Record<string, string>;
    /** 词级对齐文件（可选） */
    alignment?: string;
  };
  /** 创建时间（ISO 8601） */
  createdAt: string;

  // ========== 来自处理状态 ==========
  /** 
   * 当前处理状态
   * 
   * @deprecated 请在前端使用 isCompleted / isFailed / isProcessing 这三个语义字段，
   * 而不要直接用 state 枚举做业务判断。
   * state 仍然可以用于日志 / 调试展示，但不推荐用于分支逻辑。
   */
  state: VideoState;
  /** 处理进度（0-100） */
  progress: number;
  /** 给 UI 的简短说明 */
  message: string;
  /** 错误信息（仅在 state === 'error' 时存在） */
  error?: VideoError | null;
  /** 最后更新时间（ISO 8601） */
  updatedAt: string;

  // ========== 来自数据库 ==========
  /** 视频可见性（private: 仅本人可见, public: 所有人可见） */
  visibility?: 'private' | 'public';
  /** HLS 状态：pending / ready / error */
  hlsStatus?: 'pending' | 'ready' | 'error';
  /** HLS 播放路径，例如 /hls/<userId>/<videoId>/master.m3u8 */
  hlsPath?: string;
  /** 视频封面图片 URL */
  coverUrl?: string;
  /** 信息更新时间（数据库 updatedAt，当名称或可见性修改时更新） */
  infoUpdatedAt?: string;
  /** 上传者用户ID */
  userId?: string;
  /** 上传者用户名 */
  username?: string;

  // ========== 运行时派生（不来自任何 JSON） ==========
  /** 
   * 语义化状态字段（避免前端需要知道状态机内部结构）
   * 
   * 派生规则：
   * - isCompleted: state === 'completed'
   * - isFailed: state === 'error'
   * - isProcessing: state !== 'completed' && state !== 'error'
   */
  /** 处理是否已完成 */
  isCompleted: boolean;
  /** 处理是否失败 */
  isFailed: boolean;
  /** 处理是否进行中 */
  isProcessing: boolean;
  
  /** 
   * 视频是否可播放（语义化字段）
   * 
   * 派生规则：state === 'completed' && 
   *          capabilities.hasVideo && 
   *          capabilities.hasOriginalSubtitles && 
   *          capabilities.hasTranslatedSubtitles
   */
  playable: boolean;
  /**
   * 视频是否支持早期播放（HLS可用）
   *
   * 派生规则：hlsStatus === 'ready'
   */
  earlyPlayable: boolean;
  /** 
   * 视频能力（语义化字段）
   * 
   * 派生规则：基于 metadata.json 的 files 和文件系统检查
   */
  capabilities: {
    /** 是否有视频文件 */
    hasVideo: boolean;
    /** 是否有音频文件 */
    hasAudio: boolean;
    /** 是否有 HLS 流媒体 */
    hasHls: boolean;
    /** 是否有原文字幕 */
    hasOriginalSubtitles: boolean;
    /** 是否有翻译字幕 */
    hasTranslatedSubtitles: boolean;
    /** 是否有词级对齐数据 */
    hasWordAlignment: boolean;
  };
  /**
   * 可用的资源 URL（仅当 playable === true 时包含）
   *
   * 派生规则：基于 capabilities 和 assetResolver 生成
   */
  assetUrls?: VideoAssets;

  // ========== 前端扩展字段（后端运行时添加） ==========
  /**
   * 前端展示用的扩展字段
   * 这些字段由后端在API响应时动态添加，前端使用但不参与类型检查
   */
  _meta?: {
    /** 语言中文名称 */
    languageName: string;
    /** 时长格式化字符串（如 "3:25"） */
    durationFormatted: string;
    /** 浏览次数 */
    viewCount: number;
    /** 观看进度 (0-100)，仅用于浏览历史 */
    watchProgress?: number;
    /**
     * 视频所属合集角标
     * 仅当视频上传者自己创建的合集中包含此视频时填充；多个合集时取 position 最小者。
     */
    ownerCollection?: {
      collectionId: string;
      collectionName: string;
      position: number;
      total: number;
    };
  };
}

